/**
 * Hooks for beta feedback system.
 *
 * Provides methods to submit feedback and manage feedback for admins.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from './useAuthenticatedFetch';
import { mlbApi } from '@/services/api';
import type { FeedbackSubmission, FeedbackAdminList, FeedbackStats } from '@/types/feedback';
import { STALE_TIMES, QUERY_RETRY_CONFIG } from '@/types/statcast';

/**
 * Hook to submit beta feedback (no auth required).
 */
export function useSubmitFeedback() {
  return useMutation({
    mutationFn: (data: FeedbackSubmission) => mlbApi.submitFeedback(data),
  });
}

/**
 * Hook to fetch admin feedback list with filters.
 */
export function useAdminFeedback(params: {
  type?: string;
  category?: string;
  source?: string;
  status?: string;
  page?: number;
  per_page?: number;
}) {
  const authFetch = useAuthenticatedFetch();

  return useQuery<FeedbackAdminList>({
    queryKey: ['admin', 'feedback', params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params.type) searchParams.set('type', params.type);
      if (params.category) searchParams.set('category', params.category);
      if (params.source) searchParams.set('source', params.source);
      if (params.status) searchParams.set('status', params.status);
      if (params.page) searchParams.set('page', String(params.page));
      if (params.per_page) searchParams.set('per_page', String(params.per_page));

      return authFetch(`/api/v2/admin/feedback?${searchParams.toString()}`);
    },
    staleTime: STALE_TIMES.LIVE_DATA,
    ...QUERY_RETRY_CONFIG,
  });
}

/**
 * Hook to fetch admin feedback stats.
 */
export function useAdminFeedbackStats() {
  const authFetch = useAuthenticatedFetch();

  return useQuery<FeedbackStats>({
    queryKey: ['admin', 'feedback', 'stats'],
    queryFn: () => authFetch('/api/v2/admin/feedback/stats'),
    staleTime: STALE_TIMES.LIVE_DATA,
    ...QUERY_RETRY_CONFIG,
  });
}

/**
 * Hook to update feedback status (admin only).
 */
export function useUpdateFeedbackStatus() {
  const authFetch = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      authFetch(`/api/v2/admin/feedback/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'feedback'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'feedback', 'stats'] });
    },
  });
}
